import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaTrophy, FaMedal, FaStar, FaCrown, FaArrowLeft, 
  FaFire, FaBolt, FaRocket, FaGamepad
} from 'react-icons/fa';
import api from '../utils/api';
import { getUser } from '../utils/auth';

// Effet néon clignotant
const NeonText = ({ children, color = '#ff00ff', delay = 0 }) => (
  <motion.span
    animate={{ 
      textShadow: [
        `0 0 5px ${color}, 0 0 10px ${color}, 0 0 20px ${color}`,
        `0 0 10px ${color}, 0 0 20px ${color}, 0 0 40px ${color}`,
        `0 0 5px ${color}, 0 0 10px ${color}, 0 0 20px ${color}`
      ]
    }}
    transition={{ duration: 1.5, repeat: Infinity, delay }}
    style={{ color }}
  >
    {children}
  </motion.span>
);

function HallOfFamePage() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('global');
  const currentUser = getUser();

  const categories = [
    { id: 'global', name: 'SCORE GLOBAL', icon: FaTrophy, color: '#FFD700' },
    { id: 'quiz', name: 'QUIZ MASTER', icon: FaBolt, color: '#00FFFF' },
    { id: 'typing', name: 'SPEED TYPING', icon: FaRocket, color: '#FF00FF' },
    { id: 'streak', name: 'SÉRIE EN COURS', icon: FaFire, color: '#FF6600' }
  ];

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedCategory]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/leaderboard?category=${selectedCategory}`);
      setLeaderboard(response.data);
    } catch (error) {
      // Données de démo si l'API n'existe pas encore
      setLeaderboard([
        { rank: 1, name: 'SuperReporter_42', score: 15420, avatar: '🦸', streak: 12 },
        { rank: 2, name: 'PressQueen', score: 14890, avatar: '👑', streak: 8 },
        { rank: 3, name: 'NewsHunter', score: 13750, avatar: '🎯', streak: 15 },
        { rank: 4, name: 'TypeMaster', score: 12300, avatar: '⚡', streak: 5 },
        { rank: 5, name: 'GrammarNinja', score: 11850, avatar: '🥷', streak: 7 },
        { rank: 6, name: 'QuizChamp', score: 10920, avatar: '🏆', streak: 3 },
        { rank: 7, name: 'MediaWiz', score: 9870, avatar: '🧙', streak: 4 },
        { rank: 8, name: 'PressRookie', score: 8540, avatar: '🌟', streak: 2 },
        { rank: 9, name: 'NewsNerd', score: 7230, avatar: '🤓', streak: 6 },
        { rank: 10, name: 'JournoJunior', score: 6100, avatar: '📰', streak: 1 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <FaCrown className="text-3xl text-yellow-400" />;
      case 2: return <FaMedal className="text-3xl text-gray-300" />;
      case 3: return <FaMedal className="text-3xl text-amber-600" />;
      default: return <span className="text-2xl font-bold text-gray-400">#{rank}</span>;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1: return 'bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 border-yellow-300';
      case 2: return 'bg-gradient-to-r from-gray-500 via-gray-300 to-gray-500 border-gray-200';
      case 3: return 'bg-gradient-to-r from-amber-700 via-amber-500 to-amber-700 border-amber-400';
      default: return 'bg-gradient-to-r from-purple-900 to-indigo-900 border-purple-500';
    }
  };

  return (
    <div 
      className="min-h-screen p-4 overflow-hidden"
      style={{ 
        background: 'linear-gradient(180deg, #0a0015 0%, #1a0030 50%, #0a0015 100%)',
        fontFamily: "'Press Start 2P', monospace"
      }}
    >
      {/* Arcade cabinet frame effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-purple-900 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-purple-900 to-transparent" />
        <div className="absolute top-0 bottom-0 left-0 w-4 bg-gradient-to-r from-purple-900 to-transparent" />
        <div className="absolute top-0 bottom-0 right-0 w-4 bg-gradient-to-l from-purple-900 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-purple-800 text-white rounded border-2 border-purple-400 hover:bg-purple-700 transition-all"
            style={{ textShadow: '0 0 10px #ff00ff' }}
          >
            <FaArrowLeft className="inline mr-2" /> RETOUR
          </button>
          <div className="text-xs text-purple-400">
            <FaGamepad className="inline mr-1" /> DAILYGLOBE ARCADE
          </div>
        </div>

        {/* Title - Arcade style */}
        <motion.div 
          className="text-center mb-8"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', bounce: 0.5 }}
        >
          <h1 
            className="text-4xl md:text-6xl mb-2"
            style={{ 
              fontFamily: 'Bangers, cursive',
              letterSpacing: '4px'
            }}
          >
            <NeonText color="#FFD700" delay={0}>H</NeonText>
            <NeonText color="#FF6600" delay={0.1}>A</NeonText>
            <NeonText color="#FF00FF" delay={0.2}>L</NeonText>
            <NeonText color="#00FFFF" delay={0.3}>L</NeonText>
            <span className="mx-2" />
            <NeonText color="#00FF00" delay={0.4}>O</NeonText>
            <NeonText color="#FFD700" delay={0.5}>F</NeonText>
            <span className="mx-2" />
            <NeonText color="#FF00FF" delay={0.6}>F</NeonText>
            <NeonText color="#00FFFF" delay={0.7}>A</NeonText>
            <NeonText color="#FF6600" delay={0.8}>M</NeonText>
            <NeonText color="#00FF00" delay={0.9}>E</NeonText>
          </h1>
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-sm text-purple-300"
          >
            ★ LES MEILLEURS JOURNALISTES EN HERBE ★
          </motion.div>
        </motion.div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {categories.map((cat) => (
            <motion.button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded border-2 text-xs transition-all ${
                selectedCategory === cat.id 
                  ? 'border-white bg-purple-700' 
                  : 'border-purple-600 bg-purple-900 hover:bg-purple-800'
              }`}
              style={{ 
                color: cat.color,
                textShadow: selectedCategory === cat.id ? `0 0 10px ${cat.color}` : 'none'
              }}
            >
              <cat.icon className="inline mr-1" /> {cat.name}
            </motion.button>
          ))}
        </div>

        {/* Leaderboard */}
        <div 
          className="rounded-lg p-4 border-4"
          style={{ 
            background: 'linear-gradient(180deg, #1a0030 0%, #2a0050 100%)',
            borderColor: '#8B00FF',
            boxShadow: '0 0 20px rgba(139, 0, 255, 0.5), inset 0 0 20px rgba(139, 0, 255, 0.2)'
          }}
        >
          {loading ? (
            <div className="text-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="inline-block"
              >
                <FaStar className="text-4xl text-yellow-400" />
              </motion.div>
              <p className="text-purple-300 mt-4 text-sm">CHARGEMENT...</p>
            </div>
          ) : (
            <AnimatePresence>
              {leaderboard.map((player, index) => (
                <motion.div
                  key={player.rank}
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center justify-between p-3 mb-2 rounded border-2 ${getRankStyle(player.rank)} ${
                    currentUser?.email === player.email ? 'ring-2 ring-white ring-opacity-50' : ''
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Rank */}
                    <div className="w-12 text-center">
                      {getRankIcon(player.rank)}
                    </div>
                    
                    {/* Avatar */}
                    <div className="text-3xl">{player.avatar}</div>
                    
                    {/* Name */}
                    <div>
                      <div 
                        className="font-bold text-white text-sm md:text-base"
                        style={{ 
                          textShadow: player.rank <= 3 ? '0 0 10px rgba(255,255,255,0.5)' : 'none'
                        }}
                      >
                        {player.name}
                      </div>
                      {player.streak > 0 && (
                        <div className="text-xs text-orange-400 flex items-center">
                          <FaFire className="mr-1" /> {player.streak} jours
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Score */}
                  <motion.div 
                    className="text-right"
                    animate={player.rank === 1 ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <div 
                      className="text-xl md:text-2xl font-bold"
                      style={{ 
                        color: player.rank <= 3 ? '#FFD700' : '#FFFFFF',
                        textShadow: player.rank <= 3 ? '0 0 15px #FFD700' : 'none'
                      }}
                    >
                      {player.score.toLocaleString()}
                    </div>
                    <div className="text-xs text-purple-300">PTS</div>
                  </motion.div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Your position (if not in top 10) */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-6 p-4 rounded-lg border-2 border-dashed border-purple-500 bg-purple-900/50 text-center"
        >
          <p className="text-purple-300 text-sm mb-2">TON CLASSEMENT</p>
          <div className="flex items-center justify-center space-x-4">
            <span className="text-2xl">🎮</span>
            <div>
              <span className="text-white font-bold text-xl">#42</span>
              <span className="text-purple-400 ml-2">/ 156 élèves</span>
            </div>
            <div className="text-yellow-400 font-bold text-xl">3,250 PTS</div>
          </div>
          <p className="text-xs text-purple-400 mt-2">
            <FaRocket className="inline mr-1" /> Plus que 850 pts pour atteindre le TOP 10 !
          </p>
        </motion.div>

        {/* Motivational message */}
        <motion.div
          className="text-center mt-8"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <p className="text-sm" style={{ color: '#00FF00', textShadow: '0 0 10px #00FF00' }}>
            ★ JOUE CHAQUE JOUR POUR GRIMPER AU CLASSEMENT ★
          </p>
        </motion.div>
      </div>

      {/* Scanlines effect */}
      <div 
        className="fixed inset-0 pointer-events-none z-50 opacity-10"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)'
        }}
      />
    </div>
  );
}

export default HallOfFamePage;
